import { Account } from '@/domain/usecases'
import type { LDClient, LDFlagSet } from 'launchdarkly-react-client-sdk'
import type { FeatureFlagUserContext } from './feature-flag-types'
import { getAllFlags } from './feature-flags'

/**
 * Internal state for the feature flag client
 */
const state = {
  client: undefined as LDClient | undefined,
  flagsMap: undefined as LDFlagSet | undefined,
  isInitialized: false,
  userContext: undefined as FeatureFlagUserContext | undefined
}

/**
 * User context kind for LaunchDarkly
 */
const USER_CONTEXT_KIND = 'user'

/**
 * Build user context from account profile
 * @internal
 */
const buildUserContext = (accountProfile: Account.Model): FeatureFlagUserContext => ({
  kind: USER_CONTEXT_KIND,
  key: accountProfile.id,
  accountType: accountProfile.accountType?.name,
  onboardingCompleted: accountProfile.onboardingCompleted,
  passPlanId: accountProfile.passPlan?.id,
  language: accountProfile.preferenceLanguage,
  companyId: accountProfile.company.id,
  companyUniqueId: accountProfile.company.uniqueId,
  companyName: accountProfile.company.name
})

/**
 * Build default flags map
 * @internal
 */
const buildDefaultFlagsMap = (): LDFlagSet => {
  const defaultFlags: LDFlagSet = {}
  getAllFlags().forEach(flag => {
    defaultFlags[flag.name] = flag.defaultValue
  })
  return defaultFlags
}

/**
 * Feature flag client manager
 * Singleton instance managing LaunchDarkly client and flag state
 */
export const FeatureFlagClient = {
  /**
   * Checks if LaunchDarkly client has been initialized and flags are ready.
   * Note: LaunchDarkly is initialized with anonymous context in main.tsx,
   * so this should always be true after the app loads.
   */
  isReady(): boolean {
    return state.isInitialized && !!state.flagsMap
  },

  /**
   * Sync internal state with LaunchDarkly client
   * Called once when the app loads to sync with the already-initialized LD client
   *
   * @param ldClient - LaunchDarkly client from useLDClient()
   */
  syncWithClient(ldClient: LDClient | undefined): void {
    if (!ldClient || state.isInitialized) return

    state.client = ldClient
    state.flagsMap = ldClient.allFlags()
    state.isInitialized = true

    // Registrado uma única vez aqui para evitar acúmulo de listeners a cada login.
    ldClient.on('error', error => {
      console.error('[FeatureFlagClient] Erro no cliente LaunchDarkly:', error)
    })

    console.log('[FeatureFlagClient] ✅ Synced with LaunchDarkly client')
  },

  /**
   * Identifica o usuário no LaunchDarkly e carrega suas feature flags.
   * Chamado após login/autenticação.
   *
   * Requer que syncWithClient() tenha sido chamado antes (via useFeatureFlagSync).
   * O cliente é obtido de state.client — não precisa ser passado como parâmetro.
   *
   * @param accountProfile - Perfil da conta do usuário autenticado
   */
  async identifyUser(accountProfile: Account.Model): Promise<void> {
    if (!state.client) {
      console.error(
        '[FeatureFlagClient] identifyUser() chamado antes de syncWithClient(). ' +
          'Certifique-se de que useFeatureFlagSync() está declarado antes do useEffect que chama identifyUser().'
      )
      return
    }

    state.isInitialized = false

    try {
      const userContext = buildUserContext(accountProfile)
      state.userContext = userContext

      // identify() já aguarda o novo contexto carregar — waitForInitialization() seria redundante.
      state.flagsMap = await state.client.identify(userContext).catch(error => {
        console.error('[FeatureFlagClient] Erro ao identificar usuário no LaunchDarkly:', error)
        return buildDefaultFlagsMap()
      })

      state.isInitialized = true
    } catch (error) {
      console.error('[FeatureFlagClient] Falha ao identificar usuário:', error)
      state.flagsMap = buildDefaultFlagsMap()
      state.isInitialized = true
    }
  },

  /**
   * Update the existing LaunchDarkly user context with additional fields
   * Use this after identifyUser to enrich the context (e.g. with institutionId)
   *
   * @param partialContext - Fields to merge into the current user context
   */
  async updateContext(
    partialContext: Partial<Omit<FeatureFlagUserContext, 'kind' | 'key'>>
  ): Promise<void> {
    if (!state.client || !state.userContext) return

    const mergedContext: FeatureFlagUserContext = { ...state.userContext, ...partialContext }

    try {
      state.flagsMap = await state.client.identify(mergedContext).catch(error => {
        console.error('[FeatureFlagClient] Error updating context:', error)
        return state.flagsMap ?? buildDefaultFlagsMap()
      })
      state.userContext = mergedContext
    } catch (error) {
      console.error('[FeatureFlagClient] Failed to update context:', error)
    }
  },

  /**
   * Get the current flags map (readonly)
   * @internal - Use specific flag helpers instead
   */
  getFlagsMap(): Readonly<LDFlagSet | undefined> {
    return Object.freeze(state.flagsMap)
  },

  /**
   * Get the internal client reference
   * @internal
   */
  getClient(): LDClient | undefined {
    return state.client
  },

  /**
   * Get experiments data
   */
  getExperiments(): Record<string, unknown> {
    if (!state.client || !state.isInitialized) return {}

    const experiments: Record<string, unknown> = {}

    Object.keys(state.client!.allFlags()).forEach(flagName => {
      const detail = state.client!.variationDetail(flagName, false)
      if (detail.reason?.inExperiment) {
        experiments[flagName] = {
          inExperiment: true,
          variantName: detail.value === true ? 'B' : 'A',
          kind: detail.reason?.kind ?? 'UNKNOWN'
        }
      }
    })

    return experiments
  },

  /**
   * Reset client state (for testing or logout)
   * @internal
   */
  reset(): void {
    state.client = undefined
    state.flagsMap = undefined
    state.isInitialized = false
    state.userContext = undefined
  }
}
